import { ArrowLeft } from '@tamagui/lucide-icons';
import { Route } from '@unistocks/features/navigation/route';
import { t } from '@unistocks/shared/localization/index.ts';
import { Href } from 'expo-router';
import { useSyncExternalStore } from 'react';

function toSameOriginHref(url: string): string | null {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    const href = parsed.pathname + parsed.search + parsed.hash;
    const current = window.location.pathname + window.location.search + window.location.hash;
    return href && href !== current ? href : null;
  } catch {
    return null;
  }
}

/**
 * Previous href from the browser's session history
 */
function getNavigationApiHref(): string | null {
  try {
    const index = window?.navigation?.currentEntry?.index;
    if (navigation == null || index == null || index <= 0) return null;

    const url = navigation.entries()[index - 1]?.url;
    return url ? toSameOriginHref(url) : null;
  } catch {
    return null;
  }
}

/**
 * Same-origin `document.referrer` as a last resort for full page loads (deep
 * links, reloads) where both the Navigation API has a single entry and the
 * in-memory tracked history starts out empty.
 */
function getReferrerHref(): string | null {
  try {
    return document.referrer ? toSameOriginHref(document.referrer) : null;
  } catch {
    return null;
  }
}

function getSnapshot(): string | null {
  return getNavigationApiHref() ?? getReferrerHref();
}

function subscribe(listener: () => void): () => void {
  const navigation = window.navigation;
  navigation?.addEventListener('currententrychange', listener);
  navigation?.addEventListener('navigatesuccess', listener);
  // Traversals (back/forward) in browsers without the Navigation API.
  window.addEventListener('popstate', listener);

  return () => {
    navigation?.removeEventListener('currententrychange', listener);
    navigation?.removeEventListener('navigatesuccess', listener);
    window.removeEventListener('popstate', listener);
  };
}

/**
 * Web-only breadcrumb resolver. Returns a single "Back" route pointing at
 * the actual previous href.
 *
 * Returns `[]` when there is nowhere to go back to, in which case
 * `Breadcrumbs` renders nothing — matching the native behavior where this
 * hook always returns `[]` and the header back arrow handles navigation.
 */
export function useHistoryBreadcrumb(): Route[] {
  const href = useSyncExternalStore(subscribe, getSnapshot, () => null);

  if (!href) return [];

  return [
    {
      key: 'history-back',
      rootPathName: '',
      name: t('::Back'),
      href: href as Href,
      Icon: props => <ArrowLeft {...props} />
    }
  ];
}
